/* =========================================================================
   Terms of Service, Privacy Policy and Refund Policy.

   The texts live in src/legal/ and are DRAFTS until the lawyer approves them.
   While LEGAL_STATUS is "draft", /terms, /privacy and /refunds show a
   "draft under legal review" banner.

   LEGAL_TERMS_VERSION is what sign-up sends as the accepted version
   (users/<uid>.legalAcceptance.termsVersion). Bump it whenever the texts
   change, and set LEGAL_STATUS to "final" once the lawyer approves.

   Countries. Sign-up asks for the country the person lives in and allows
   only SIGNUP_COUNTRIES: the US and its territories, Mexico and Costa Rica.
   Mexico and Costa Rica are offered only once LEGAL_STATUS is "final"
   (offeredSignupCountries): their sign-up notices need the business address
   and a privacy contact, which the drafts leave blank. The backend has the
   same gate on STUDIO_LEGAL_FINAL (backend/src/services/legalAcceptance.js).
   Courses are still sold in the US and its territories only. The backend
   holds the same lists in backend/src/services/countries.js; change both
   together.

   COURSE_ELIGIBILITY is the 18+ / US box on the course checkout. The backend
   refuses a checkout without it and versions that statement on its own
   (ELIGIBILITY_VERSION in backend/src/services/checkoutEligibility.js).
   CHECKOUT_ELIGIBILITY is the old name, kept so older imports still work.
   studioEligibility(country) is the 18+ / country box on a Studio plan
   checkout (legal.studioEligibility.<country> in the translations).
   ========================================================================= */

export const LEGAL_STATUS = "draft";                 // "final" once the lawyer approves
export const LEGAL_TERMS_VERSION = "2026-11-02-draft-5";  // draft-5: DMCA agent "Copyright Agent", refunds within 5 business days, Putnam County (draft-4: seller contact details)
export const LEGAL_PATHS = { terms: "/terms", privacy: "/privacy", refunds: "/refunds" };
export const ACCESSIBILITY_PATH = "/accessibility";   // the accessibility statement, not a policy

// Same codes as backend/src/services/countries.js.
export const US_AND_TERRITORIES = Object.freeze(["US", "PR", "GU", "VI", "AS", "MP", "UM"]);
export const SIGNUP_COUNTRIES = Object.freeze([...US_AND_TERRITORIES, "MX", "CR"]);
export const COURSE_COUNTRIES = US_AND_TERRITORIES;
// The sign-up select's "Another country" choice. Never sent to the backend.
export const OTHER_COUNTRY = "other";

export const isSignupCountry = (code) => SIGNUP_COUNTRIES.includes(code);

// Sign-up from Mexico and Costa Rica opens only once the legal texts are
// final (the "final texts have no blanks" test in src/legal/legalDocs.test.js
// runs then). While they are drafts the residence picker lists only the US
// and its territories. `status` defaults to LEGAL_STATUS; the sign-up
// components pass LEGAL_STATUS explicitly, so a test can open the gate by
// mocking it.
export const mxCrSignupOpen = (status = LEGAL_STATUS) => status === "final";
export const offeredSignupCountries = (status = LEGAL_STATUS) =>
  mxCrSignupOpen(status) ? SIGNUP_COUNTRIES : US_AND_TERRITORIES;
export const isCourseCountry = (code) => COURSE_COUNTRIES.includes(code);

// The Studio group of a country: "US" (the US and its territories), "MX",
// "CR", or null.
export const countryGroup = (code) => {
  const c = String(code || "").trim().toUpperCase();
  if (US_AND_TERRITORIES.includes(c)) return "US";
  if (c === "MX" || c === "CR") return c;
  return null;
};

// The notice sign-up shows a Mexico or Costa Rica resident, and the id the
// backend stores with the acceptance (legalAcceptance.privacyNotice).
export const PRIVACY_NOTICES = Object.freeze({
  MX: "mx-simplified-2026-10-29",
  CR: "cr-consent-2026-10-29",
});
export const privacyNoticeFor = (country) => PRIVACY_NOTICES[country] || undefined;

/* The contact details the Mexico notice and the Costa Rica consent show
   (LFPDPPP arts. 15 I and IV, through 16 II; Reglamento 37554 arts. 4-5):
   the address of CODENEC, LLC, the person or department that answers privacy
   requests, and the email to reach them. They fill the [BUSINESS ADDRESS],
   [PRIVACY CONTACT] and [CONTACT EMAIL] blanks of the texts quoted in
   src/legal/privacy.es.js (sections 16 and 17), which sign-up must show word
   for word.

   Set them in .env.production (CRA inlines REACT_APP_* at build time, so a
   change needs a build and a deploy):
     REACT_APP_CODENEC_BUSINESS_ADDRESS
     REACT_APP_CODENEC_PRIVACY_CONTACT
     REACT_APP_CODENEC_CONTACT_EMAIL

   Until all three are set, sign-up stays closed to Mexico and Costa Rica:
   a notice with a blank would lack an item the law requires.

   The backend stores only the notice's id (PRIVACY_NOTICES below), so each id
   must always mean one text: when the notice wording or these details change
   after people have signed up, give the notice a new id here AND in
   backend/src/services/legalAcceptance.js. */
const filled = (value) => {
  const text = String(value ?? "").trim();
  // "[BUSINESS ADDRESS]" copied from the policies is still a blank.
  return text && !/[[\]]/.test(text) ? text : "";
};

export const legalContact = (env = process.env) => ({
  address: filled(env.REACT_APP_CODENEC_BUSINESS_ADDRESS),
  privacyContact: filled(env.REACT_APP_CODENEC_PRIVACY_CONTACT),
  contactEmail: filled(env.REACT_APP_CODENEC_CONTACT_EMAIL),
});

// Whether sign-up can show this country's notice in full. Countries with no
// notice of their own (the US and its territories) always can; Mexico and
// Costa Rica need the final texts (mxCrSignupOpen) and all three details.
export const residenceNoticeReady = (country, env = process.env, status = LEGAL_STATUS) => {
  if (country !== "MX" && country !== "CR") return true;
  if (!mxCrSignupOpen(status)) return false;
  const { address, privacyContact, contactEmail } = legalContact(env);
  return Boolean(address && privacyContact && contactEmail);
};

// Mexico and Costa Rica read the Spanish documents, whatever the site
// language: their links carry ?doc=es.
export const docLangFor = (country) => (country === "MX" || country === "CR" ? "es" : undefined);

// A policy path, with ?doc=<lang> when one is given and an optional #anchor.
export const legalPath = (item, docLang, hash) =>
  `${LEGAL_PATHS[item]}${docLang ? `?doc=${docLang}` : ""}${hash ? `#${hash}` : ""}`;

// The acceptance sign-up sends (users/<uid>.legalAcceptance on the backend).
export const legalAcceptance = ({ residenceCountry, locale, privacyNotice } = {}) => ({
  termsVersion: LEGAL_TERMS_VERSION,
  ageConfirmed: true,
  residenceCountry,
  locale,
  ...(privacyNotice ? { privacyNotice } : {}),
});

export const COURSE_ELIGIBILITY = Object.freeze({ adult: true, usResident: true });
export const CHECKOUT_ELIGIBILITY = COURSE_ELIGIBILITY;
export const studioEligibility = (country) => ({ adult: true, country });
